import { useLayoutEffect, useRef, useState, type HTMLAttributes, type ReactNode } from 'react'
import { createLayoutResizeObserver } from '@features/shared/utils/layoutResizeObserver'
import type { ActiveModule } from '../models'
import { useGlobalSidebarStore } from '../stores/useGlobalSidebarStore'
import { measureRailAnchor } from '../logic/railAnchor'
import { placeRailCard } from '../logic/railCard'
import { SidebarMenuResizeHandle } from './SidebarMenuResizeHandle'

/**
 * Where a card beside the rail starts: on the page card's edge, the cards' gap
 * past the rail's outer edge, where a pinned menu's card starts too, so
 * pinning a menu never moves it sideways. The gap is padding, so the pointer
 * crossing it from the rail is already on the card's wrapper.
 */
export const RAIL_FLYOUT_LEFT_CLASS = 'left-full pl-[var(--page-card-gap-left)]'

/** How a card beside the rail looks: an area's menu, or an item's name. */
export const RAIL_FLYOUT_CARD_CLASS =
  'rounded-[var(--page-card-radius)] border border-[color:var(--page-card-border-color)] bg-[color:var(--surface-sidebar-submenu)] shadow-lg'

/** A pinned menu: a card on the frame beside the page, which makes room for it. */
const PINNED_CARD_CLASS =
  'module-drawer app-frame-panel-start isolate relative flex flex-col overflow-hidden bg-[color:var(--surface-sidebar-submenu)] text-[color:var(--sidebar-submenu-text-subtle)] transition-shadow duration-200'

/** The menu's first item to line up with the area's icon. */
const FIRST_ITEM_SELECTOR = '[data-sidebar-list-viewport] :is(a, button, [role="button"])'

interface Placement {
  top: number
  maxHeight: number | undefined
}

/**
 * An area's menu as a card beside its icon in the rail, over the page (Menus:
 * Opens on hover). Its first item lines up with the icon (centre to centre),
 * kept between the page card's top and bottom edges; it is as tall as its
 * content up to that, so a long Tables list scrolls inside it. While the
 * pointer or focus is in it, it stays where it is (still within the page
 * card): a filter that drops its first rows mustn't pull it from under the
 * pointer; it lines up again when next opened. It fades in and out, and takes
 * no pointer while closed. Moving from the icon into it never leaves the
 * sidebar, and it follows the rail as the rail widens.
 *
 * Pinned, the same menu is a card on the frame beside the page instead, from
 * the page card's top to its bottom. While the rail widens over the page
 * (`floating`), the pinned card rides beside it over the page too, with a
 * hover menu's shadow; the page stays where it rests. It's the same elements either way, so
 * pinning or unpinning restyles the menu where it is: nothing is drawn anew,
 * nothing fades, and a list keeps its scroll. Unpinned with the pointer still
 * on it, it stays where the pinned card was.
 */
export function ModuleFlyout({
  activeModule,
  isOpen,
  pinned = false,
  floating = false,
  width,
  children,
  ...cardProps
}: {
  activeModule: ActiveModule | null
  isOpen: boolean
  /** A card beside the page (Menus: Always open) rather than over it. */
  pinned?: boolean
  /** Pinned, carried over the page by the widened rail. */
  floating?: boolean
  /** A CSS length: the Tables list is wider than an area's page list. */
  width: string
  children: ReactNode
} & Omit<HTMLAttributes<HTMLElement>, 'children'>) {
  const wrapperRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLElement>(null)
  const [placement, setPlacement] = useState<Placement>({ top: 0, maxHeight: undefined })
  // The menu, open, that was last placed: only it may stay where it is.
  const placedRef = useRef<ActiveModule | null>(null)

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    const card = cardRef.current
    const layer = wrapper?.offsetParent
    if (!wrapper || !card || !(layer instanceof HTMLElement) || !activeModule) return

    const place = () => {
      const { tileCenter, top: minTop, room: maxHeight } = measureRailAnchor(layer, activeModule)
      let desiredTop = 0
      if (tileCenter !== null) {
        const itemRect = card.querySelector(FIRST_ITEM_SELECTOR)?.getBoundingClientRect()
        const itemCenter = itemRect
          ? itemRect.top + itemRect.height / 2 - card.getBoundingClientRect().top
          : 0
        desiredTop = tileCenter - itemCenter
      }
      const bottomLimit = minTop + Math.max(0, maxHeight - card.offsetHeight)
      // Only a menu already open holds its place while in use; another
      // area's menu, or this one opening again, lines up with its icon.
      const holds =
        useGlobalSidebarStore.getState().drawerInUse && placedRef.current === activeModule
      placedRef.current = isOpen ? activeModule : null
      setPlacement((current) => {
        // Pinned, it runs from the page card's top, so unpinning (with the
        // pointer still on it, so in use) leaves it right there.
        const from = pinned ? minTop : holds ? current.top : desiredTop
        const top = placeRailCard(from, bottomLimit, minTop)
        return current.top === top && current.maxHeight === maxHeight ? current : { top, maxHeight }
      })
    }

    place()
    // Placed before the browser paints, so it never shows in the wrong spot.
    const observer = createLayoutResizeObserver(place)
    observer.observe(card)
    observer.observe(layer)
    return () => observer.disconnect()
  }, [activeModule, isOpen, pinned])

  if (!activeModule) return null

  return (
    <div
      ref={wrapperRef}
      // Hovering, it fades in as it opens, also when it is drawn already open
      // (rail-flyout-fade); pinned, it's simply there.
      data-shown={isOpen}
      className={
        pinned
          ? 'absolute inset-y-0 left-full flex'
          : `rail-flyout-fade absolute ${RAIL_FLYOUT_LEFT_CLASS} z-10`
      }
      style={
        pinned
          ? // The resize handle sits in the middle of the card's gap from the page.
            { ['--side-panel-handle-offset' as string]: 'calc(var(--page-card-gap) / 2)' }
          : { top: placement.top }
      }
    >
      <aside
        ref={cardRef}
        // The rail reads where an open menu is, for the pointer's aim (menuAim.ts).
        data-rail-menu={isOpen || undefined}
        {...cardProps}
        className={
          pinned
            ? `${PINNED_CARD_CLASS} ${floating ? 'shadow-lg' : 'shadow-none'}`
            : `module-drawer module-drawer--flyout relative flex flex-col overflow-hidden text-[color:var(--sidebar-submenu-text-subtle)] ${RAIL_FLYOUT_CARD_CLASS}`
        }
        style={pinned ? { width } : { width, maxHeight: placement.maxHeight }}
      >
        {children}
      </aside>
      {pinned && <SidebarMenuResizeHandle cardRef={cardRef} />}
    </div>
  )
}
